// ===========================================
// Invoice Amendment History (2026-09-26)
// ===========================================
// The audit trail of a SENT invoice's amendments: who, when, the family-facing
// reason, the total before → after, what changed, and whether the family was
// emailed — with a Resend on the LATEST amendment only (an older notice would
// quote superseded figures). Dates are dd/mm/yyyy in the school's zone.
//
// 2026-09-30: one stacked block per amendment instead of a seven-column table —
// the table overflowed its card and clipped the email chip and Resend button.
// Resend is DISABLED once the invoice is no longer SENT/OVERDUE (voided, paid):
// the backend (`resendAmendmentNotice`) already 400s it there.

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import { useInvoiceAmendments, useResendAmendmentNotice } from '../../hooks/useInvoices';
import { formatInSchoolTz } from '../../utils/dateUtils';
import { formatCurrencyFromDollars } from '../../utils/currency';
import { describeAmendmentChanges } from '../../utils/invoiceAmend';
import type { InvoiceAmendment, InvoiceStatus } from '../../api/invoices.api';

interface InvoiceAmendmentHistoryProps {
  invoiceId: string;
  timeZone: string;
  /** The viewer may re-send the notice (operations_invoices:write). */
  canResend: boolean;
  /** The invoice's current status — the notice can only be re-sent while it is SENT or OVERDUE. */
  invoiceStatus: InvoiceStatus;
}

const dmy = { day: '2-digit', month: '2-digit', year: 'numeric' } as const;

function NotifiedChip({ amendment, timeZone }: { amendment: InvoiceAmendment; timeZone: string }) {
  if (amendment.notifiedAt) {
    return (
      <Chip size="small" color="success" label={`Emailed ${formatInSchoolTz(amendment.notifiedAt, timeZone, dmy)}`} />
    );
  }
  if (amendment.notifyResult?.skipped === 'ADMIN_CHOSE_NOT_TO_NOTIFY') {
    return <Chip size="small" label="Not emailed" />;
  }
  if (amendment.notifyResult) {
    return <Chip size="small" color="error" label="Email not delivered" />;
  }
  return <Chip size="small" label="Not emailed" />;
}

/** "+$10.00" / "−$25.00" / "no change" */
function totalDelta(before: string, after: string): string {
  const diff = Number(after) - Number(before);
  if (Math.abs(diff) < 0.005) return 'no change';
  return `${diff > 0 ? '+' : '−'}${formatCurrencyFromDollars(Math.abs(diff))}`;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Box>
  );
}

export default function InvoiceAmendmentHistory({
  invoiceId,
  timeZone,
  canResend,
  invoiceStatus,
}: InvoiceAmendmentHistoryProps) {
  const { data: amendments, isLoading, isError } = useInvoiceAmendments(invoiceId);
  const resend = useResendAmendmentNotice();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (isError) {
    return <Alert severity="error">Could not load the amendment history.</Alert>;
  }
  if (!amendments || amendments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This invoice has not been amended.
      </Typography>
    );
  }

  const latestId = amendments[0].id; // newest first
  const invoiceOpen = invoiceStatus === 'SENT' || invoiceStatus === 'OVERDUE';
  const closedReason = invoiceStatus === 'CANCELLED' ? 'The invoice is void.' : 'The invoice is no longer open.';

  return (
    <Stack divider={<Divider flexItem />} spacing={2}>
      {amendments.map((a) => {
        const changes = describeAmendmentChanges(a.before, a.after);
        return (
          <Box key={a.id} data-testid={`amendment-${a.amendmentNumber}`}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">
                Amendment {a.amendmentNumber} · {formatInSchoolTz(a.createdAt, timeZone, dmy)} · {a.performedByName}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <NotifiedChip amendment={a} timeZone={timeZone} />
                {canResend && a.id === latestId && (
                  <Button
                    size="small"
                    disabled={resend.isPending || !invoiceOpen}
                    onClick={() => resend.mutate({ invoiceId, amendmentId: a.id })}
                  >
                    {a.notifiedAt ? 'Resend email' : 'Send email'}
                  </Button>
                )}
                {canResend && a.id === latestId && !invoiceOpen && (
                  <Typography variant="caption" color="text.secondary">
                    {closedReason}
                  </Typography>
                )}
              </Box>
            </Box>

            <Stack spacing={0.75}>
              <Field label="Total">
                <Typography variant="body2">
                  {formatCurrencyFromDollars(a.before.total)} → {formatCurrencyFromDollars(a.after.total)}{' '}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({totalDelta(a.before.total, a.after.total)})
                  </Typography>
                </Typography>
              </Field>
              <Field label="Reason">
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {a.reason}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Shown to the family
                </Typography>
              </Field>
              {changes.length > 0 && (
                <Field label="Changes">
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {changes.map((line) => (
                      <Typography key={line} component="li" variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Field>
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
